/* eslint-disable */
/**
 * TradeCalendar
 *
 * mode="asset"     — single asset, props: entries[], currentPrice
 * mode="dashboard" — all assets,   props: assetData[] (each has .entries[], .livePrice, .ticker)
 */
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { C } from "../shared/globals.js";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const COLORS = [C.orange, "#60a5fa", "#a78bfa", C.green, "#f472b6", "#34d399", "#fbbf24", "#fb7185"];

const fmt$ = v => v >= 1000
  ? "$" + (v / 1000).toFixed(1) + "k"
  : "$" + v.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });

const fmtFull = v => "$" + v.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });

const fmtShares = v => {
  if (v >= 1000) return v.toLocaleString("en-US", { maximumFractionDigits:2 });
  if (v < 0.001) return v.toFixed(6);
  if (v < 1)     return v.toFixed(4);
  return v.toFixed(2);
};

// ── Shared tooltip box ────────────────────────────────────────────────────────
const TT_STYLE = {
  background:"#1a1a1a",
  border:`1px solid ${C.orange}`,
  borderRadius:8,
  padding:"10px 14px",
  fontFamily:"Montserrat,sans-serif",
  boxShadow:"0 8px 24px rgba(0,0,0,0.9)",
};

// ── Nav header ────────────────────────────────────────────────────────────────
function CalHeader({ calMonth, calYear, onPrev, onNext, onToday, now, info }) {
  const isCurrentMonth = calMonth === now.getMonth() && calYear === now.getFullYear();
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
      <button onClick={onPrev} style={{
        background:"transparent", border:"1px solid var(--border)", borderRadius:6,
        width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", color:"var(--text4)", flexShrink:0,
      }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
        <i className="ph ph-caret-left" style={{ fontSize:12 }}/>
      </button>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:"var(--text)", minWidth:140, textAlign:"center" }}>
        {MONTHS[calMonth]} {calYear}
      </div>
      <button onClick={onNext} style={{
        background:"transparent", border:"1px solid var(--border)", borderRadius:6,
        width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", color:"var(--text4)", flexShrink:0,
      }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
        <i className="ph ph-caret-right" style={{ fontSize:12 }}/>
      </button>
      {!isCurrentMonth && (
        <button onClick={onToday} style={{
          background:"transparent", border:"1px solid var(--border)", borderRadius:6,
          padding:"4px 10px", cursor:"pointer", fontFamily:"Montserrat,sans-serif",
          fontSize:8, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em",
        }}>TODAY</button>
      )}
      {info && <div style={{ marginLeft:"auto", fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>{info}</div>}
    </div>
  );
}

// ── Day header row ────────────────────────────────────────────────────────────
function DayHeaders() {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:3, marginBottom:3 }}>
      {DAYS.map(d => (
        <div key={d} style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text4)", textAlign:"center", letterSpacing:"0.06em", padding:"4px 0" }}>{d}</div>
      ))}
    </div>
  );
}

// ── ASSET MODE ────────────────────────────────────────────────────────────────
function AssetCalendar({ entries, currentPrice }) {
  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [tooltip,  setTooltip]  = useState(null);
  const mouseRef   = useRef({ x:0, y:0 });
  const tooltipRef = useRef(null);

  const prevMonth  = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y=>y-1); } else setCalMonth(m=>m-1); };
  const nextMonth  = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y=>y+1); } else setCalMonth(m=>m+1); };
  const goToday    = () => { setCalMonth(now.getMonth()); setCalYear(now.getFullYear()); };

  const updatePos  = (x, y) => {
    const el = tooltipRef.current;
    if (!el) return;
    const TW = 200;
    el.style.left = (x + 18 + TW > window.innerWidth ? x - TW - 10 : x + 14) + "px";
    el.style.top  = (y - 12) + "px";
  };

  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const hasCp       = currentPrice > 0;

  const byDate = {};
  entries.forEach(e => {
    if (!e.date) return;
    if (!byDate[e.date]) byDate[e.date] = { dollars:0, shares:0, count:0 };
    byDate[e.date].dollars += e.dollars || 0;
    byDate[e.date].shares  += e.shares  || 0;
    byDate[e.date].count   += 1;
  });

  const monthEntries = Object.entries(byDate).filter(([d]) => {
    const dt = new Date(d);
    return dt.getMonth() === calMonth && dt.getFullYear() === calYear;
  });
  const maxDollars = Math.max(...monthEntries.map(([,v]) => v.dollars), 1);
  const monthTotal = monthEntries.reduce((s,[,v]) => s + v.dollars, 0);
  const info = `${monthEntries.length} trade${monthEntries.length !== 1 ? "s" : ""}${monthEntries.length > 0 ? " · " + fmt$(monthTotal) + " invested" : " this month"}`;

  return (
    <div style={{ position:"relative" }}>
      <CalHeader calMonth={calMonth} calYear={calYear} onPrev={prevMonth} onNext={nextMonth} onToday={goToday} now={now} info={info}/>
      <DayHeaders/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:3 }}>
        {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} style={{ height:52 }}/>)}
        {Array(daysInMonth).fill(null).map((_, i) => {
          const day      = i + 1;
          const dateStr  = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const trade    = byDate[dateStr];
          const isToday  = day === now.getDate() && calMonth === now.getMonth() && calYear === now.getFullYear();
          const hasTrade = !!trade;
          const intensity = hasTrade ? Math.max(0.15, trade.dollars / maxDollars) : 0;
          let pnlPct = null, pnlDollars = null, isProfit = null;
          if (hasTrade && hasCp && trade.shares > 0) {
            pnlDollars = trade.shares * currentPrice - trade.dollars;
            pnlPct     = (pnlDollars / trade.dollars) * 100;
            isProfit   = pnlDollars >= 0;
          }
          return (
            <div key={day}
              onMouseEnter={e => { if (!hasTrade) return; mouseRef.current={x:e.clientX,y:e.clientY}; setTooltip({dateStr,trade,pnlPct,pnlDollars,isProfit}); requestAnimationFrame(()=>updatePos(e.clientX,e.clientY)); }}
              onMouseLeave={() => setTooltip(null)}
              onMouseMove={e => { if (hasTrade) updatePos(e.clientX, e.clientY); }}
              style={{
                height:52, borderRadius:6,
                border:`1px solid ${isToday?"#60a5fa":hasTrade?"rgba(242,135,5,0.3)":"var(--border)"}`,
                background: hasTrade?`rgba(242,135,5,${intensity*0.25})`:isToday?"rgba(96,165,250,0.07)":"var(--bg3)",
                cursor:hasTrade?"pointer":"default",
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                gap:1,padding:"4px 2px",position:"relative",transition:"all 0.1s",
              }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:isToday||hasTrade?800:500, color:isToday?"#60a5fa":hasTrade?"var(--text)":"var(--text4)", lineHeight:1 }}>{day}</div>
              {isToday && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:7, fontWeight:700, color:"#60a5fa", letterSpacing:"0.08em", lineHeight:1 }}>TODAY</div>}
              {hasTrade && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:C.orange }}>{fmt$(trade.dollars)}</div>}
              {hasTrade && hasCp && isProfit !== null && <div style={{ width:5, height:5, borderRadius:"50%", background:isProfit?C.green:C.red, position:"absolute", top:4, right:4 }}/>}
            </div>
          );
        })}
      </div>

      {tooltip && createPortal((() => {
        const { dateStr, trade, pnlPct, pnlDollars, isProfit } = tooltip;
        const label = new Date(dateStr).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", year:"numeric" });
        const { x, y } = mouseRef.current;
        return (
          <div ref={tooltipRef} style={{ position:"fixed", left:(x+18+200>window.innerWidth?x-210:x+14), top:y-12, zIndex:9999, pointerEvents:"none", whiteSpace:"nowrap" }}>
            <div style={TT_STYLE}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a0a0a0", marginBottom:8 }}>{label}</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <div><div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>INVESTED</div><div style={{ fontSize:15, fontWeight:700, color:C.orange }}>{fmtFull(trade.dollars)}</div></div>
                <div style={{ width:"100%", height:1, background:"#2c2c2c" }}/>
                <div><div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>SHARES</div><div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>{fmtShares(trade.shares)}</div></div>
                {trade.count > 1 && <><div style={{ width:"100%", height:1, background:"#2c2c2c" }}/><div><div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>TRADES</div><div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>{trade.count}</div></div></>}
                {pnlPct !== null && <><div style={{ width:"100%", height:1, background:"#2c2c2c" }}/><div><div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>P&L</div><div style={{ fontSize:15, fontWeight:800, color:isProfit?C.green:C.red }}>{isProfit?"+":""}{pnlPct.toFixed(2)}%</div><div style={{ fontSize:12, fontWeight:700, color:isProfit?C.green:C.red, marginTop:1 }}>{isProfit?"+":""}${Math.abs(pnlDollars).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</div></div></>}
              </div>
            </div>
          </div>
        );
      })(), document.body)}
    </div>
  );
}

// ── DASHBOARD MODE ────────────────────────────────────────────────────────────
function DashboardCalendar({ assetData }) {
  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [tooltip,  setTooltip]  = useState(null);
  const mouseRef   = useRef({ x:0, y:0 });
  const tooltipRef = useRef(null);

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y=>y-1); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y=>y+1); } else setCalMonth(m=>m+1); };
  const goToday   = () => { setCalMonth(now.getMonth()); setCalYear(now.getFullYear()); };

  const updatePos = (x, y) => {
    const el = tooltipRef.current;
    if (!el) return;
    const TW = 230;
    el.style.left = (x + 18 + TW > window.innerWidth ? x - TW - 10 : x + 14) + "px";
    el.style.top  = (y - 12) + "px";
  };

  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();

  // Build per-date map: { [dateStr]: [ { ticker, color, dollars, shares, livePrice } ] }
  const byDate = {};
  assetData.forEach((asset, ai) => {
    const color = COLORS[ai % COLORS.length];
    (asset.entries || []).forEach(e => {
      if (!e.date) return;
      if (!byDate[e.date]) byDate[e.date] = [];
      byDate[e.date].push({ ticker: asset.ticker, color, dollars: e.dollars||0, shares: e.shares||0, livePrice: asset.livePrice||0 });
    });
  });

  const monthDays  = Object.entries(byDate).filter(([d]) => { const dt=new Date(d); return dt.getMonth()===calMonth && dt.getFullYear()===calYear; });
  const monthTotal = monthDays.reduce((s,[,t]) => s + t.reduce((ss,x)=>ss+x.dollars,0), 0);
  const monthCount = monthDays.reduce((s,[,t]) => s + t.length, 0);
  const maxDollars = Math.max(...monthDays.map(([,t]) => t.reduce((s,x)=>s+x.dollars,0)), 1);
  const info = `${monthCount} trade${monthCount!==1?"s":""}${monthCount>0?" · "+fmt$(monthTotal)+" invested":" this month"}`;

  return (
    <div style={{ position:"relative" }}>
      <CalHeader calMonth={calMonth} calYear={calYear} onPrev={prevMonth} onNext={nextMonth} onToday={goToday} now={now} info={info}/>
      <DayHeaders/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:3 }}>
        {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} style={{ height:64 }}/>)}
        {Array(daysInMonth).fill(null).map((_, i) => {
          const day      = i + 1;
          const dateStr  = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const trades   = byDate[dateStr] || [];
          const isToday  = day===now.getDate() && calMonth===now.getMonth() && calYear===now.getFullYear();
          const hasTrade = trades.length > 0;
          const dayTotal = trades.reduce((s,t) => s+t.dollars, 0);
          const intensity = hasTrade ? Math.max(0.12, dayTotal/maxDollars) : 0;
          const tickers  = [...new Set(trades.map(t=>t.ticker))];

          return (
            <div key={day}
              onMouseEnter={e => { if(!hasTrade) return; mouseRef.current={x:e.clientX,y:e.clientY}; setTooltip({dateStr,trades,dayTotal}); requestAnimationFrame(()=>updatePos(e.clientX,e.clientY)); }}
              onMouseLeave={() => setTooltip(null)}
              onMouseMove={e => { if(hasTrade) updatePos(e.clientX,e.clientY); }}
              style={{
                height:64, borderRadius:6,
                border:`1px solid ${isToday?"#60a5fa":hasTrade?"rgba(242,135,5,0.3)":"var(--border)"}`,
                background: hasTrade?`rgba(242,135,5,${intensity*0.22})`:isToday?"rgba(96,165,250,0.07)":"var(--bg3)",
                cursor:hasTrade?"pointer":"default",
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                gap:2,padding:"4px 2px",position:"relative",transition:"all 0.1s",overflow:"hidden",
              }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:isToday||hasTrade?800:500, color:isToday?"#60a5fa":hasTrade?"var(--text)":"var(--text4)", lineHeight:1 }}>{day}</div>
              {isToday && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:7, fontWeight:700, color:"#60a5fa", letterSpacing:"0.08em", lineHeight:1 }}>TODAY</div>}
              {hasTrade && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:C.orange }}>{fmt$(dayTotal)}</div>}
              {/* Colored dots per ticker */}
              {hasTrade && (
                <div style={{ display:"flex", gap:2, flexWrap:"wrap", justifyContent:"center", maxWidth:"100%", paddingTop:1 }}>
                  {tickers.slice(0,5).map((ticker,ti) => {
                    const t = trades.find(x=>x.ticker===ticker);
                    return <div key={ticker} style={{ width:5, height:5, borderRadius:"50%", background:t?.color||COLORS[ti%COLORS.length], flexShrink:0 }}/>;
                  })}
                  {tickers.length>5 && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:6, color:"var(--text4)", lineHeight:"5px" }}>+{tickers.length-5}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {tooltip && createPortal((() => {
        const { dateStr, trades, dayTotal } = tooltip;
        const label = new Date(dateStr).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", year:"numeric" });
        const { x, y } = mouseRef.current;
        const TW = 230;

        // Group by ticker
        const byTicker = {};
        trades.forEach(t => {
          if (!byTicker[t.ticker]) byTicker[t.ticker] = { dollars:0, shares:0, color:t.color, livePrice:t.livePrice };
          byTicker[t.ticker].dollars += t.dollars;
          byTicker[t.ticker].shares  += t.shares;
        });

        return (
          <div ref={tooltipRef} style={{ position:"fixed", left:(x+18+TW>window.innerWidth?x-TW-10:x+14), top:y-12, zIndex:9999, pointerEvents:"none", whiteSpace:"nowrap" }}>
            <div style={TT_STYLE}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a0a0a0", marginBottom:8 }}>{label}</div>
              {/* Total */}
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>TOTAL INVESTED</div>
                <div style={{ fontSize:15, fontWeight:700, color:C.orange }}>{fmtFull(dayTotal)}</div>
              </div>
              <div style={{ width:"100%", height:1, background:"#2c2c2c", marginBottom:8 }}/>
              {/* Per asset */}
              <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                {Object.entries(byTicker).map(([ticker, data]) => {
                  const hasCp    = data.livePrice > 0;
                  const pnlD     = hasCp ? data.shares * data.livePrice - data.dollars : null;
                  const pnlPct   = pnlD !== null ? (pnlD / data.dollars) * 100 : null;
                  const isProfit = pnlD !== null ? pnlD >= 0 : null;
                  return (
                    <div key={ticker}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:data.color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, fontWeight:800, color:"#fff" }}>{ticker}</span>
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, color:C.orange }}>{fmtFull(data.dollars)}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:2, paddingLeft:11 }}>
                        <span style={{ fontSize:9, color:"#888" }}>{fmtShares(data.shares)} shares</span>
                        {pnlPct !== null && <span style={{ fontSize:9, fontWeight:700, color:isProfit?C.green:C.red }}>{isProfit?"+":""}{pnlPct.toFixed(2)}%</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })(), document.body)}
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────
export default function TradeCalendar({ mode = "asset", entries, currentPrice, assetData }) {
  if (mode === "dashboard") return <DashboardCalendar assetData={assetData || []} />;
  return <AssetCalendar entries={entries || []} currentPrice={currentPrice || 0} />;
}
